import groupBy from "lodash/groupBy";

export const ConsentControllerFactory = ({
  consentService,
}: {
  consentService: IConsentService;
}) => {
  const getConsentList: IGetConsentList = async ({ clinicId, search }) => {
    const [consentListResponse, consentDischargeListResponse] =
      await Promise.all([
        consentService.getConsentList({
          clinicId,
          search,
        }),
        consentService.getConsentDischargeList({
          clinicId,
          search,
        }),
      ]);

    const {
      success: consentListSuccess,
      data: consentListData,
      error: consentListError,
    } = consentListResponse;
    const { success: consentDischargeSuccess, data: consentDischargeData } =
      consentDischargeListResponse;

    if (
      consentListSuccess &&
      consentListData &&
      consentDischargeSuccess &&
      consentDischargeData
    ) {
      const dischargedConsentList: Array<IConsent & { isDischarged: boolean }> =
        [];
      consentDischargeData.data.forEach((resident) => {
        const { admission } = resident;
        const recordWithDischargedKey = {
          ...resident,
          isDischarged: true,
        };
        if (admission.consented_status !== 2) {
          dischargedConsentList.push(recordWithDischargedKey);
        }
      });

      let data = [...consentListData.data, ...dischargedConsentList];

      data = data.map((item) => {
        if (item.admission.consented_status === null) {
          item.admission.consented_status = 2;
        }
        return item;
      });

      data.sort((a, b) =>
        a.patient.last_name.localeCompare(b.patient.last_name)
      );

      const response = groupBy(data, (item) => {
        return item.admission.consented_status;
      });
      return {
        success: true,
        data: { data: response },
      };
    }
    return { success: false, error: consentListError };
  };

  const approveConsent: IApproveConsent = async ({
    clinicId,
    consentStatus,
    consentedDatetime,
    file,
    residentId,
  }) => {
    const response = await consentService.setConsentChange({
      clinic_id: clinicId,
      consented_datetime: consentedDatetime,
      file,
      consented_status: consentStatus,
      patient_id: residentId,
      residentType: "Active",
    });

    return response;
  };

  const denyArchiveConsent: IDenyArchiveConsent = async ({
    clinicId,
    consentStatus,
    consentedDatetime,
    residentId,
  }) => {
    const response = await consentService.setConsentChange({
      clinic_id: clinicId,
      consented_datetime: consentedDatetime,
      consented_status: consentStatus,
      patient_id: residentId,
      residentType: "Active",
    });

    return response;
  };

  const eSignPatientConsent: IESignPatientConsent = async ({
    clinicId,
    consentStatus,
    consentedDatetime,
    residentId,
    patientConsent,
  }) => {
    const response = await consentService.setConsentChange({
      clinic_id: clinicId,
      consented_datetime: consentedDatetime,
      consented_status: consentStatus,
      patient_id: residentId,
      residentType: "Active", // Remove from all after confirmation
      digital_signature: {
        date: consentedDatetime,
        patient_signature: patientConsent,
      },
    });

    return response;
  };

  const eSignFacilityConsent: IESignFacilityConsent = async ({
    clinicId,
    consentStatus,
    consentedDatetime,
    residentId,
    facilityConsent,
  }) => {
    const response = await consentService.setConsentChange({
      clinic_id: clinicId,
      consented_datetime: consentedDatetime,
      consented_status: consentStatus,
      patient_id: residentId,
      residentType: "Active", // Remove from all after confirmation
      digital_signature: {
        date: consentedDatetime,
        facility_signature: facilityConsent,
      },
    });

    return response;
  };

  return {
    getConsentList,
    approveConsent,
    denyArchiveConsent,
    eSignPatientConsent,
    eSignFacilityConsent,
  };
};
